import { EncodingResult } from '@mode-vegas/vegas';
import { ChartTypes, CappieReporterTypes, DataProfilingTypes, SortSettingsTypes } from '@mode/shared/contract-common';
import { EmbedActions } from '../embed.actions';
import { FilterPanelActions } from '../filter-panel.actions';
import { EmbedReportState } from './embed-report.state';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace EmbedReportActions {
  export enum Type {
    UpdateState = 'Embed.UpdateState',
    LoadState = 'Embed.LoadState',
    ResetState = 'Embed.ResetState',
    DocumentClick = 'Embed.DocumentClick',
    DocumentKeydown = 'Embed.DocumentKeydown',
    Autogrow = 'Embed.Autogrow',
    SetTableOffset = 'Embed.SetTableOffset',
    SetTableColumnSearch = 'Embed.SetTableColumnSearch',
    LoadThemeSuccess = 'Embed.loadThemeSuccess',
    UpdateEncoding = 'Embed.UpdateEncoding',
    AlamodeCustomizeTable = 'Embed.AlamodeCustomizeTable',
    ReportRendered = 'Embed.ReportRendered',
    ProgressReporting = '[Cappie Reporter] Progress Reported', // used to digest into an action outside of the iframe
    ReportHasScrolled = 'Embed.ReportHasScrolled',
    ReportStartRender = 'Embed.ReportStartRender',
    UpdateVizIntersections = 'Embed.UpdateVizIntersection',
    ExportChartCsv = 'Embed.ExportChartCsv',
    GetDataProfiling = 'Embed.GetDataProfiling',
    UpdateSortSettings = 'Embed.UpdateSortSettings',
    ViewUnaggregatedData = 'ViewUnaggregatedData',
  }

  export class UpdateReportFeatureState {
    readonly type = Type.UpdateState;
    constructor(public readonly delta: any) {}
  }

  export class LoadReportFeatureState {
    readonly type = Type.LoadState;
    constructor(public readonly state: EmbedReportState.SerializedReportState) {}
  }

  export class ResetReportFeatureState {
    readonly type = Type.ResetState;
    constructor(public readonly reportLayout: string) {}
  }

  export class DocumentClick {
    readonly type = Type.DocumentClick;
  }

  export class DocumentKeydown {
    readonly type = Type.DocumentKeydown;
    constructor(
      public readonly keyCode: number,
      public readonly key: string,
      public readonly metaKey: boolean,
      public readonly altKey: boolean,
      public readonly ctrlKey: boolean
    ) {}
  }

  export class Autogrow {
    readonly type = Type.Autogrow;
    constructor(
      public readonly height: number,
      public readonly width: number
    ) {}
  }

  export class SetTableOffset {
    readonly type = Type.SetTableOffset;

    constructor(
      public readonly token: string,
      public readonly offset: number
    ) {}
  }

  export class SetTableColumnSearch {
    readonly type = Type.SetTableColumnSearch;

    constructor(
      public readonly token: string,
      public readonly columnSearchModel: ChartTypes.TableColumnSearch
    ) {}
  }

  export class LoadThemeSuccess {
    readonly type = Type.LoadThemeSuccess;
  }

  export class UpdateEncoding {
    readonly type = Type.UpdateEncoding;

    constructor(
      public readonly token: string,
      public readonly encResult: EncodingResult,
      public readonly pillAction?: ChartTypes.PillActionType
    ) {}
  }

  export class ExportChartCsv {
    readonly type = Type.ExportChartCsv;

    constructor(public readonly token: string) {}
  }

  export class AlamodeCustomizeTable {
    readonly type = Type.AlamodeCustomizeTable;

    constructor(public readonly payload: ChartTypes.FlatTableUserProvidedSettings[]) {}
  }

  export class ReportStartRendered {
    readonly type = Type.ReportStartRender;

    constructor(public readonly virtualized = false) {}
  }

  export class ReportRendered {
    readonly type = Type.ReportRendered;

    constructor() {}
  }

  export class ProgressReporting {
    readonly type = Type.ProgressReporting;

    constructor(public readonly eventData: CappieReporterTypes.ProgressData) {}
  }

  export class ReportHasScrolled {
    readonly type = Type.ReportHasScrolled;

    constructor() {}
  }

  export class UpdateVizIntersections {
    readonly type = Type.UpdateVizIntersections;

    constructor(public readonly intersections: Record<string, boolean>) {}
  }

  export class GetDataProfiling {
    readonly type = Type.GetDataProfiling;

    constructor(
      public token: string,
      public cols: DataProfilingTypes.DataProfilingColInfo[],
      public referenceId: string
    ) {}
  }

  export class UpdateSortSettings {
    readonly type = Type.UpdateSortSettings;

    constructor(
      public token: string,
      public event: SortSettingsTypes.SortSettingsEvent
    ) {}
  }

  export class ViewUnaggregatedDataAction {
    readonly type = Type.ViewUnaggregatedData;
    constructor(
      public chartToken: string,
      public queryToken: string
    ) {}
  }

  export type Actions =
    | LoadReportFeatureState
    | ResetReportFeatureState
    | DocumentClick
    | DocumentKeydown
    | Autogrow
    | UpdateReportFeatureState
    | SetTableOffset
    | SetTableColumnSearch
    | LoadThemeSuccess
    | UpdateEncoding
    | AlamodeCustomizeTable
    | ReportRendered
    | ProgressReporting
    | ReportHasScrolled
    | ReportStartRendered
    | UpdateVizIntersections
    | FilterPanelActions.Actions
    | EmbedActions
    | ExportChartCsv
    | UpdateSortSettings
    | GetDataProfiling
    | ViewUnaggregatedDataAction;
}
